/* eslint-disable @typescript-eslint/no-use-before-define */

import { useCallback } from 'react';
import { useQuery } from '@apollo/client';

import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import SuspenseQuery from 'components/SuspenseQuery';
import OBLoaderPage from 'features/Onboarding/components/OBLoaderPage/OBLoaderPage';
import { ApplicantType } from 'features/Onboarding/types';
import logger from 'helpers/logger/logger';

import { VerificationStatus } from '../Verification/lib/steps/types';
import { PersonalAddressDocument } from './graphql/queries/personalAddress.gql';
import getModuleStatus from './lib/getModuleStatus';
import machineConfig, { model, State } from './machine';
import Address from './pages/Address';
import AddressRecap from './pages/AddressRecap';

type PersonalInfoProps = {
  applicantType: ApplicantType;
  companyProfileId?: string;
};

const PersonalAddress = asBentoModule<PersonalInfoProps>(
  ({ applicantType, companyProfileId, onDone }) => {
    const query = useQuery(PersonalAddressDocument, {
      variables: {
        companyProfileId: companyProfileId ?? '',
        includeCompany: Boolean(companyProfileId),
      },
    });

    return (
      <SuspenseQuery
        loader={<OBLoaderPage onPrev={() => null} />}
        query={query}
      >
        {(personalAddressData) => (
          <PersonalAddressComponent
            moduleData={{
              applicantType,
              personalAddressData,
            }}
            onDone={onDone}
          />
        )}
      </SuspenseQuery>
    );
  },
);

const PersonalAddressComponent = withStateMachine<typeof model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;

    const { applicantType, personalAddressData } = moduleData;
    const { profile } = personalAddressData.viewer;

    const onPrev = useCallback(() => send(model.events.PREV()), [send]);

    const stringifiedState = stringifyState<State>(state);

    switch (stringifiedState) {
      case State.ADDRESS_FORM:
      case State.SAVING_ADDRESS:
        return (
          <Address
            defaultAddress={profile}
            isSaving={stringifiedState === State.SAVING_ADDRESS}
            onContinue={(payload) => send(model.events.SUBMIT_ADDRESS(payload))}
            onPrev={onPrev}
          />
        );

      case State.ADDRESS_RECAP: {
        const moduleStatus = getModuleStatus({
          applicantType,
          userProfile: profile,
        });

        if (moduleStatus !== VerificationStatus.Completed) {
          logger.error(
            `${State.ADDRESS_RECAP} state reached but address module is not completed`,
          );
        }

        return (
          <AddressRecap
            defaultAddress={profile}
            onContinue={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );
      }

      case State.DONE:
      case State.ABORT:
        return <OBLoaderPage onPrev={() => null} />;

      default:
        return assertNever(stringifiedState);
    }
  },
  {
    devTools: true,
    machineConfig,
  },
);

export default PersonalAddress;
